"use client";

import { useEffect, useRef, useState } from "react";
import AppImage from "@/components/ui/AppImage";
import Link from "next/link";

interface TimelinePhase {
  phase: string;
  phaseZh: string;
  duration: string;
  status: "completed" | "in-progress" | "upcoming";
  milestones: string[];
}

interface BudgetItem {
  category: string;
  categoryZh: string;
  amount: number;
  percentage: number;
}

interface TeamMember {
  name: string;
  nameZh: string;
  role: string;
  roleZh: string;
  avatar: string;
}

const PROJECT_DATA = {
  titleEn: "Taikoo Shing Luxury Residence",
  titleZh: "太古城豪華住宅翻新",
  location: "Taikoo Shing, Hong Kong Island",
  locationZh: "香港島太古城",
  area: "1,200 sq ft",
  duration: "5 months",
  completionDate: "December 2024",
  category: "Residential Renovation",
  categoryZh: "住宅翻新",
  client: "Private Client",
  clientZh: "私人客戶",
  overviewEn: "A comprehensive renovation transforming a dated 1,200 sq ft apartment into a modern luxury residence. The project focused on creating an open-plan living space with premium finishes, custom furniture, and smart home integration. Key features include a redesigned kitchen with marble countertops, spa-inspired bathrooms, and floor-to-ceiling windows maximizing natural light and harbor views.",
  overviewZh: "全面翻新1,200平方呎舊式單位，打造成現代豪華住宅。項目重點是創建開放式生活空間，配備高級飾面、訂造傢俬及智能家居系統。主要特色包括重新設計的大理石廚房、水療風格浴室，以及落地玻璃窗，最大化自然光線及海港景觀。",
  beforeImage: "https://img.rocket.new/generatedImages/rocket_gen_img_134fe04c0-1767823605296.png",
  beforeAlt: "Before renovation: dated apartment with old fixtures and closed layout",
  afterImage: "https://images.unsplash.com/photo-1721523237013-0d66d9ccabf6",
  afterAlt: "After renovation: modern luxury open-plan living room with contemporary design",
  totalBudget: 850000
};

const TIMELINE: TimelinePhase[] = [
{
  phase: "Planning & Design",
  phaseZh: "規劃設計",
  duration: "4 weeks",
  status: "completed",
  milestones: [
  "Initial consultation and site survey",
  "3D design renders approved",
  "Material selection finalized",
  "Building permits obtained"]

},
{
  phase: "Demolition & Structural",
  phaseZh: "拆卸結構",
  duration: "3 weeks",
  status: "completed",
  milestones: [
  "Removed non-load bearing walls",
  "Upgraded electrical wiring",
  "Replaced plumbing systems",
  "Reinforced floor structure"]

},
{
  phase: "Installation & Fitting",
  phaseZh: "安裝裝配",
  duration: "8 weeks",
  status: "completed",
  milestones: [
  "Kitchen and bathroom installation",
  "Custom carpentry and built-ins",
  "Flooring and wall finishes",
  "Smart home system integration"]

},
{
  phase: "Finishing Touches",
  phaseZh: "最後修飾",
  duration: "3 weeks",
  status: "completed",
  milestones: [
  "Painting and decorative finishes",
  "Lighting fixture installation",
  "Furniture placement",
  "Final cleaning and inspection"]

},
{
  phase: "Handover & Warranty",
  phaseZh: "交樓保修",
  duration: "2 weeks",
  status: "completed",
  milestones: [
  "Client walkthrough completed",
  "Documentation provided",
  "2-year warranty activated",
  "Aftercare support initiated"]

}];


const BUDGET: BudgetItem[] = [
{ category: "Design & Planning", categoryZh: "設計規劃", amount: 85000, percentage: 10 },
{ category: "Demolition & Structural", categoryZh: "拆卸結構", amount: 127500, percentage: 15 },
{ category: "Kitchen Renovation", categoryZh: "廚房翻新", amount: 170000, percentage: 20 },
{ category: "Bathroom Renovation", categoryZh: "浴室翻新", amount: 127500, percentage: 15 },
{ category: "Flooring & Finishes", categoryZh: "地板飾面", amount: 102000, percentage: 12 },
{ category: "Custom Furniture", categoryZh: "訂造傢俬", amount: 119000, percentage: 14 },
{ category: "Electrical & Smart Home", categoryZh: "電力智能", amount: 76500, percentage: 9 },
{ category: "Contingency & Misc", categoryZh: "應急雜項", amount: 42500, percentage: 5 }];


const TEAM: TeamMember[] = [
{
  name: "Michael Chan",
  nameZh: "陳志明",
  role: "Lead Designer",
  roleZh: "首席設計師",
  avatar: "https://i.pravatar.cc/150?img=12"
},
{
  name: "Sarah Wong",
  nameZh: "黃美玲",
  role: "Project Manager",
  roleZh: "項目經理",
  avatar: "https://i.pravatar.cc/150?img=45"
},
{
  name: "David Lee",
  nameZh: "李建華",
  role: "Master Carpenter",
  roleZh: "資深木工",
  avatar: "https://i.pravatar.cc/150?img=33"
},
{
  name: "Emily Tam",
  nameZh: "譚詠欣",
  role: "Interior Stylist",
  roleZh: "室內造型師",
  avatar: "https://i.pravatar.cc/150?img=47"
}];


export default function ProjectShowcase() {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const comparisonRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    budgetRange: "",
    timeline: "",
    serviceTypes: [] as string[],
    projectScope: "",
    specialRequirements: ""
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (service: string) => {
    setFormData(prev => ({
      ...prev,
      serviceTypes: prev.serviceTypes.includes(service)
        ? prev.serviceTypes.filter(s => s !== service)
        : [...prev.serviceTypes, service]
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const message = `*Project Inquiry - ${PROJECT_DATA.titleEn}*%0A%0A` +
      `*Client Details:*%0A` +
      `Name: ${formData.name}%0A` +
      `Email: ${formData.email}%0A` +
      `Phone: ${formData.phone}%0A` +
      `Company: ${formData.company || "N/A"}%0A%0A` +
      `*Budget & Timeline:*%0A` +
      `Budget Range: ${formData.budgetRange}%0A` +
      `Timeline: ${formData.timeline}%0A%0A` +
      `*Project Interests:*%0A` +
      `Services: ${formData.serviceTypes.join(", ") || "N/A"}%0A` +
      `Scope: ${formData.projectScope}%0A` +
      `Special Requirements: ${formData.specialRequirements || "N/A"}`;
    
    window.open(`https://wa.me/85256614547?text=${message}`, "_blank");
  };

  useEffect(() => {
    const elements = sectionRef.current?.querySelectorAll(".proj-reveal");
    if (!elements) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.remove("hidden-reveal");
        });
      },
      { threshold: 0.1 }
    );
    elements.forEach((el) => {
      el.classList.add("hidden-reveal");
      observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const handleSliderMove = (clientX: number) => {
    if (!comparisonRef.current) return;
    const rect = comparisonRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, x / rect.width * 100));
    setSliderPosition(percentage);
  };

  const handleMouseDown = () => setIsDragging(true);
  const handleMouseUp = () => setIsDragging(false);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) handleSliderMove(e.clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length > 0) handleSliderMove(e.touches[0].clientX);
  };

  return (
    <section ref={sectionRef} className="w-full">
      {/* Hero Header */}
      <div className="relative w-full h-[60vh] min-h-[400px] overflow-hidden">
        <AppImage
          src={PROJECT_DATA.afterImage}
          alt={PROJECT_DATA.afterAlt}
          fill
          priority
          className="object-cover" />
        
        <div
          className="absolute inset-0 z-10"
          style={{ background: "linear-gradient(to top, rgba(10,22,40,0.95) 0%, rgba(10,22,40,0.4) 60%, transparent 100%)" }} />
        
        
        {/* Back Button */}
        <div className="absolute top-6 left-6 z-20">
          <Link
            href="/homepage"
            className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-all duration-300"
            style={{
              background: "rgba(212,175,55,0.15)",
              color: "#D4AF37",
              border: "1px solid rgba(212,175,55,0.3)"
            }}>
            
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Back · 返回
          </Link>
        </div>

        {/* Title Overlay */}
        <div className="absolute bottom-0 left-0 right-0 z-20 px-6 md:px-12 pb-12">
          <div className="max-w-screen-xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-[1px]" style={{ background: "#D4AF37" }} />
              <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "#D4AF37" }}>
                {PROJECT_DATA.category} · {PROJECT_DATA.categoryZh}
              </span>
            </div>
            <h1 className="font-display text-4xl md:text-6xl font-bold text-white mb-3">
              {PROJECT_DATA.titleEn}
            </h1>
            <p className="text-xl md:text-2xl" style={{ color: "#D4AF37" }}>
              {PROJECT_DATA.titleZh}
            </p>
          </div>
        </div>
      </div>

      {/* Project Overview */}
      <div className="py-16 md:py-24 px-6 md:px-12" style={{ background: "linear-gradient(180deg, #0A1628 0%, #0D1E3A 100%)" }}>
        <div className="max-w-screen-xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Overview Text */}
            <div className="lg:col-span-2 proj-reveal reveal">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-[1px]" style={{ background: "#D4AF37" }} />
                <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "#D4AF37" }}>
                  Project Overview · 項目概覽
                </span>
              </div>
              <p className="text-base leading-relaxed mb-6" style={{ color: "rgba(245,240,232,0.85)" }}>
                {PROJECT_DATA.overviewEn}
              </p>
              <p className="text-sm leading-relaxed" style={{ color: "rgba(245,240,232,0.6)" }}>
                {PROJECT_DATA.overviewZh}
              </p>
            </div>

            {/* Project Details */}
            <div className="proj-reveal reveal">
              <div
                className="p-6 rounded-2xl"
                style={{ background: "rgba(26,47,94,0.3)", border: "1px solid rgba(212,175,55,0.2)" }}>
                
                <h3 className="font-display text-xl font-bold text-white mb-6">Project Details</h3>
                <div className="space-y-4">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: "#D4AF37" }}>
                      Location · 地點
                    </div>
                    <div className="text-sm" style={{ color: "rgba(245,240,232,0.85)" }}>
                      {PROJECT_DATA.location}
                    </div>
                    <div className="text-xs" style={{ color: "rgba(245,240,232,0.5)" }}>
                      {PROJECT_DATA.locationZh}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: "#D4AF37" }}>
                      Area · 面積
                    </div>
                    <div className="text-sm" style={{ color: "rgba(245,240,232,0.85)" }}>
                      {PROJECT_DATA.area}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: "#D4AF37" }}>
                      Duration · 工期
                    </div>
                    <div className="text-sm" style={{ color: "rgba(245,240,232,0.85)" }}>
                      {PROJECT_DATA.duration}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: "#D4AF37" }}>
                      Completion · 完工日期
                    </div>
                    <div className="text-sm" style={{ color: "rgba(245,240,232,0.85)" }}>
                      {PROJECT_DATA.completionDate}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: "#D4AF37" }}>
                      Client · 客戶
                    </div>
                    <div className="text-sm" style={{ color: "rgba(245,240,232,0.85)" }}>
                      {PROJECT_DATA.client}
                    </div>
                    <div className="text-xs" style={{ color: "rgba(245,240,232,0.5)" }}>
                      {PROJECT_DATA.clientZh}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Before/After Comparison */}
      <div className="py-16 md:py-24 px-6 md:px-12" style={{ background: "#0A1628" }}>
        <div className="max-w-screen-xl mx-auto">
          <div className="text-center mb-12 proj-reveal reveal">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-8 h-[1px]" style={{ background: "#D4AF37" }} />
              <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "#D4AF37" }}>
                Transformation · 改造對比
              </span>
              <div className="w-8 h-[1px]" style={{ background: "#D4AF37" }} />
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-bold text-white">
              Before & <span className="italic" style={{ color: "#D4AF37" }}>After</span>
            </h2>
          </div>

          {/* Image Comparison Slider */}
          <div
            ref={comparisonRef}
            className="relative w-full h-[500px] md:h-[600px] rounded-2xl overflow-hidden cursor-ew-resize proj-reveal reveal"
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleMouseDown}
            onTouchEnd={handleMouseUp}
            onTouchMove={handleTouchMove}>
            
            {/* After Image (Full) */}
            <div className="absolute inset-0">
              <AppImage
                src={PROJECT_DATA.afterImage}
                alt={PROJECT_DATA.afterAlt}
                fill
                className="object-cover" />
              
              <div className="absolute top-6 right-6 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider"
              style={{ background: "rgba(212,175,55,0.9)", color: "#0A1628" }}>
                After · 翻新後
              </div>
            </div>

            {/* Before Image (Clipped) */}
            <div
              className="absolute inset-0"
              style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}>
              
              <AppImage
                src={PROJECT_DATA.beforeImage}
                alt={PROJECT_DATA.beforeAlt}
                fill
                className="object-cover" />
              
              <div className="absolute top-6 left-6 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider"
              style={{ background: "rgba(26,47,94,0.9)", color: "#D4AF37" }}>
                Before · 翻新前
              </div>
            </div>

            {/* Slider Handle */}
            <div
              className="absolute top-0 bottom-0 w-1 cursor-ew-resize"
              style={{
                left: `${sliderPosition}%`,
                background: "#D4AF37",
                boxShadow: "0 0 20px rgba(212,175,55,0.6)"
              }}>
              
              <div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full flex items-center justify-center"
                style={{ background: "#D4AF37", boxShadow: "0 4px 20px rgba(212,175,55,0.4)" }}>
                
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0A1628" strokeWidth="2">
                  <path d="M18 8L22 12L18 16" />
                  <path d="M6 8L2 12L6 16" />
                </svg>
              </div>
            </div>
          </div>

          <p className="text-center text-sm mt-6" style={{ color: "rgba(245,240,232,0.5)" }}>
            Drag the slider to compare · 拖動滑桿查看對比
          </p>
        </div>
      </div>

      {/* Project Timeline */}
      <div className="py-16 md:py-24 px-6 md:px-12" style={{ background: "linear-gradient(180deg, #0D1E3A 0%, #0A1628 100%)" }}>
        <div className="max-w-screen-xl mx-auto">
          <div className="text-center mb-12 proj-reveal reveal">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-8 h-[1px]" style={{ background: "#D4AF37" }} />
              <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "#D4AF37" }}>
                Project Timeline · 項目時間表
              </span>
              <div className="w-8 h-[1px]" style={{ background: "#D4AF37" }} />
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-bold text-white">
              Construction <span className="italic" style={{ color: "#D4AF37" }}>Phases</span>
            </h2>
          </div>

          <div className="space-y-8">
            {TIMELINE.map((phase, index) =>
            <div
              key={index}
              className="proj-reveal reveal">
              
                <div
                className="p-6 md:p-8 rounded-2xl"
                style={{ background: "rgba(26,47,94,0.3)", border: "1px solid rgba(212,175,55,0.2)" }}>
                
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
                    <div className="flex items-center gap-4 mb-4 md:mb-0">
                      <div
                      className="w-12 h-12 rounded-full flex items-center justify-center font-display font-bold text-lg"
                      style={{ background: "linear-gradient(135deg, #D4AF37, #A8891E)", color: "#0A1628" }}>
                      
                        {index + 1}
                      </div>
                      <div>
                        <h3 className="font-display text-xl md:text-2xl font-bold text-white">
                          {phase.phase}
                        </h3>
                        <p className="text-sm" style={{ color: "#D4AF37" }}>
                          {phase.phaseZh}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="text-xs font-bold uppercase tracking-wider" style={{ color: "#D4AF37" }}>
                          Duration
                        </div>
                        <div className="text-sm" style={{ color: "rgba(245,240,232,0.85)" }}>
                          {phase.duration}
                        </div>
                      </div>
                      <div
                      className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider"
                      style={{
                        background: phase.status === "completed" ? "rgba(34,197,94,0.2)" : "rgba(212,175,55,0.2)",
                        color: phase.status === "completed" ? "#22C55E" : "#D4AF37",
                        border: `1px solid ${phase.status === "completed" ? "rgba(34,197,94,0.3)" : "rgba(212,175,55,0.3)"}`
                      }}>
                      
                        {phase.status === "completed" ? "✓ Completed" : "In Progress"}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {phase.milestones.map((milestone, mIndex) =>
                  <div key={mIndex} className="flex items-start gap-3">
                        <div className="w-1.5 h-1.5 rounded-full mt-2" style={{ background: "#D4AF37" }} />
                        <span className="text-sm" style={{ color: "rgba(245,240,232,0.7)" }}>
                          {milestone}
                        </span>
                      </div>
                  )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Budget Breakdown */}
      <div className="py-16 md:py-24 px-6 md:px-12" style={{ background: "#0A1628" }}>
        <div className="max-w-screen-xl mx-auto">
          <div className="text-center mb-12 proj-reveal reveal">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-8 h-[1px]" style={{ background: "#D4AF37" }} />
              <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "#D4AF37" }}>
                Budget Breakdown · 預算分配
              </span>
              <div className="w-8 h-[1px]" style={{ background: "#D4AF37" }} />
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-bold text-white mb-4">
              Investment <span className="italic" style={{ color: "#D4AF37" }}>Allocation</span>
            </h2>
            <div className="font-display text-4xl md:text-5xl font-bold" style={{ color: "#D4AF37" }}>
              HK$ {PROJECT_DATA.totalBudget.toLocaleString()}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {BUDGET.map((item, index) =>
            <div
              key={index}
              className="proj-reveal reveal">
              
                <div
                className="p-6 rounded-2xl"
                style={{ background: "rgba(26,47,94,0.3)", border: "1px solid rgba(212,175,55,0.2)" }}>
                
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-bold text-white text-lg">{item.category}</h3>
                      <p className="text-xs" style={{ color: "rgba(245,240,232,0.5)" }}>
                        {item.categoryZh}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="font-display text-xl font-bold" style={{ color: "#D4AF37" }}>
                        {item.percentage}%
                      </div>
                      <div className="text-sm" style={{ color: "rgba(245,240,232,0.7)" }}>
                        HK$ {item.amount.toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <div className="w-full h-2 rounded-full" style={{ background: "rgba(26,47,94,0.5)" }}>
                    <div
                    className="h-full rounded-full transition-all duration-1000"
                    style={{
                      width: `${item.percentage}%`,
                      background: "linear-gradient(90deg, #D4AF37, #E8CC6A)"
                    }} />
                  
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Team Credits */}
      <div className="py-16 md:py-24 px-6 md:px-12" style={{ background: "linear-gradient(180deg, #0D1E3A 0%, #0A1628 100%)" }}>
        <div className="max-w-screen-xl mx-auto">
          <div className="text-center mb-12 proj-reveal reveal">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-8 h-[1px]" style={{ background: "#D4AF37" }} />
              <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "#D4AF37" }}>
                Team Credits · 團隊成員
              </span>
              <div className="w-8 h-[1px]" style={{ background: "#D4AF37" }} />
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-bold text-white">
              Meet the <span className="italic" style={{ color: "#D4AF37" }}>Team</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {TEAM.map((member, index) =>
            <div
              key={index}
              className="text-center proj-reveal reveal">
              
                <div
                className="p-6 rounded-2xl"
                style={{ background: "rgba(26,47,94,0.3)", border: "1px solid rgba(212,175,55,0.2)" }}>
                
                  <div className="w-24 h-24 mx-auto mb-4 rounded-full overflow-hidden" style={{ border: "3px solid #D4AF37" }}>
                    <img
                    src={member.avatar}
                    alt={`${member.name} - ${member.role}`}
                    className="w-full h-full object-cover" />
                  
                  </div>
                  <h3 className="font-display text-xl font-bold text-white mb-1">
                    {member.name}
                  </h3>
                  <p className="text-sm mb-2" style={{ color: "rgba(245,240,232,0.6)" }}>
                    {member.nameZh}
                  </p>
                  <div
                  className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider"
                  style={{ background: "rgba(212,175,55,0.2)", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.3)" }}>
                  
                    {member.role}
                  </div>
                  <p className="text-xs mt-2" style={{ color: "rgba(245,240,232,0.4)" }}>
                    {member.roleZh}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Project Inquiry Form */}
      <div className="py-16 md:py-24 px-6 md:px-12" style={{ background: "linear-gradient(180deg, #0A1628 0%, #0D1E3A 100%)" }}>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12 proj-reveal reveal">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-8 h-[1px]" style={{ background: "#D4AF37" }} />
              <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "#D4AF37" }}>
                Project Inquiry · 項目查詢
              </span>
              <div className="w-8 h-[1px]" style={{ background: "#D4AF37" }} />
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-bold text-white mb-4">
              Interested in a <span className="italic" style={{ color: "#D4AF37" }}>Similar Project?</span>
            </h2>
            <p className="text-lg" style={{ color: "rgba(245,240,232,0.7)" }}>
              讓我們了解您的需求 · Tell us about your requirements
            </p>
          </div>

          <form onSubmit={handleSubmit} className="proj-reveal reveal">
            <div
              className="p-8 md:p-10 rounded-2xl"
              style={{ background: "rgba(26,47,94,0.3)", border: "1px solid rgba(212,175,55,0.2)" }}>
              
              {/* Client Details */}
              <div className="mb-8">
                <h3 className="font-display text-xl font-bold text-white mb-6 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: "#D4AF37", color: "#0A1628" }}>
                    1
                  </div>
                  Client Details · 客戶資料
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold mb-2" style={{ color: "#D4AF37" }}>
                      Full Name · 姓名 *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg text-white focus:outline-none focus:ring-2 transition-all"
                      style={{
                        background: "rgba(10,22,40,0.5)",
                        border: "1px solid rgba(212,175,55,0.3)",
                        focusRing: "rgba(212,175,55,0.5)"
                      }}
                      placeholder="Enter your name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold mb-2" style={{ color: "#D4AF37" }}>
                      Email · 電郵 *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg text-white focus:outline-none focus:ring-2 transition-all"
                      style={{
                        background: "rgba(10,22,40,0.5)",
                        border: "1px solid rgba(212,175,55,0.3)"
                      }}
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold mb-2" style={{ color: "#D4AF37" }}>
                      Phone · 電話 *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg text-white focus:outline-none focus:ring-2 transition-all"
                      style={{
                        background: "rgba(10,22,40,0.5)",
                        border: "1px solid rgba(212,175,55,0.3)"
                      }}
                      placeholder="+852 1234 5678"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold mb-2" style={{ color: "#D4AF37" }}>
                      Company · 公司 (Optional)
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg text-white focus:outline-none focus:ring-2 transition-all"
                      style={{
                        background: "rgba(10,22,40,0.5)",
                        border: "1px solid rgba(212,175,55,0.3)"
                      }}
                      placeholder="Company name"
                    />
                  </div>
                </div>
              </div>

              {/* Budget & Timeline */}
              <div className="mb-8">
                <h3 className="font-display text-xl font-bold text-white mb-6 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: "#D4AF37", color: "#0A1628" }}>
                    2
                  </div>
                  Budget & Timeline · 預算與時間
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold mb-2" style={{ color: "#D4AF37" }}>
                      Budget Range · 預算範圍 *
                    </label>
                    <select
                      name="budgetRange"
                      required
                      value={formData.budgetRange}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg text-white focus:outline-none focus:ring-2 transition-all"
                      style={{
                        background: "rgba(10,22,40,0.5)",
                        border: "1px solid rgba(212,175,55,0.3)"
                      }}>
                      <option value="" style={{ background: "#0A1628" }}>Select budget range</option>
                      <option value="HK$ 300,000 - 500,000" style={{ background: "#0A1628" }}>HK$ 300,000 - 500,000</option>
                      <option value="HK$ 500,000 - 800,000" style={{ background: "#0A1628" }}>HK$ 500,000 - 800,000</option>
                      <option value="HK$ 800,000 - 1,200,000" style={{ background: "#0A1628" }}>HK$ 800,000 - 1,200,000</option>
                      <option value="HK$ 1,200,000 - 2,000,000" style={{ background: "#0A1628" }}>HK$ 1,200,000 - 2,000,000</option>
                      <option value="HK$ 2,000,000+" style={{ background: "#0A1628" }}>HK$ 2,000,000+</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-bold mb-2" style={{ color: "#D4AF37" }}>
                      Timeline Expectations · 預期時間 *
                    </label>
                    <select
                      name="timeline"
                      required
                      value={formData.timeline}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg text-white focus:outline-none focus:ring-2 transition-all"
                      style={{
                        background: "rgba(10,22,40,0.5)",
                        border: "1px solid rgba(212,175,55,0.3)"
                      }}>
                      <option value="" style={{ background: "#0A1628" }}>Select timeline</option>
                      <option value="Urgent (1-2 months)" style={{ background: "#0A1628" }}>Urgent (1-2 months)</option>
                      <option value="Standard (3-4 months)" style={{ background: "#0A1628" }}>Standard (3-4 months)</option>
                      <option value="Flexible (5-6 months)" style={{ background: "#0A1628" }}>Flexible (5-6 months)</option>
                      <option value="Long-term (6+ months)" style={{ background: "#0A1628" }}>Long-term (6+ months)</option>
                      <option value="Planning phase" style={{ background: "#0A1628" }}>Planning phase</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Project Interests */}
              <div className="mb-8">
                <h3 className="font-display text-xl font-bold text-white mb-6 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: "#D4AF37", color: "#0A1628" }}>
                    3
                  </div>
                  Project Interests · 項目興趣
                </h3>
                
                {/* Service Types */}
                <div className="mb-6">
                  <label className="block text-sm font-bold mb-3" style={{ color: "#D4AF37" }}>
                    Service Types · 服務類型 (Select all that apply)
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      { en: "Full Renovation", zh: "全屋翻新" },
                      { en: "Kitchen Remodel", zh: "廚房改造" },
                      { en: "Bathroom Upgrade", zh: "浴室升級" },
                      { en: "Custom Furniture", zh: "訂造傢俬" },
                      { en: "Smart Home Integration", zh: "智能家居" },
                      { en: "Interior Design", zh: "室內設計" }
                    ].map((service) => (
                      <label
                        key={service.en}
                        className="flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all"
                        style={{
                          background: formData.serviceTypes.includes(service.en)
                            ? "rgba(212,175,55,0.2)"
                            : "rgba(10,22,40,0.3)",
                          border: `1px solid ${formData.serviceTypes.includes(service.en) ? "rgba(212,175,55,0.5)" : "rgba(212,175,55,0.2)"}`
                        }}>
                        <input
                          type="checkbox"
                          checked={formData.serviceTypes.includes(service.en)}
                          onChange={() => handleCheckboxChange(service.en)}
                          className="w-5 h-5 rounded accent-[#D4AF37]"
                        />
                        <span className="text-sm" style={{ color: "rgba(245,240,232,0.85)" }}>
                          {service.en} · {service.zh}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Project Scope */}
                <div className="mb-6">
                  <label className="block text-sm font-bold mb-2" style={{ color: "#D4AF37" }}>
                    Project Scope · 項目範圍 *
                  </label>
                  <textarea
                    name="projectScope"
                    required
                    value={formData.projectScope}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-4 py-3 rounded-lg text-white focus:outline-none focus:ring-2 transition-all resize-none"
                    style={{
                      background: "rgba(10,22,40,0.5)",
                      border: "1px solid rgba(212,175,55,0.3)"
                    }}
                    placeholder="Describe your project scope, size, and key requirements..."
                  />
                </div>

                {/* Special Requirements */}
                <div>
                  <label className="block text-sm font-bold mb-2" style={{ color: "#D4AF37" }}>
                    Special Requirements · 特殊要求 (Optional)
                  </label>
                  <textarea
                    name="specialRequirements"
                    value={formData.specialRequirements}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-4 py-3 rounded-lg text-white focus:outline-none focus:ring-2 transition-all resize-none"
                    style={{
                      background: "rgba(10,22,40,0.5)",
                      border: "1px solid rgba(212,175,55,0.3)"
                    }}
                    placeholder="Any specific materials, styles, or special considerations..."
                  />
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="w-full py-4 rounded-full font-bold text-lg transition-all duration-300 hover:scale-[1.02] flex items-center justify-center gap-3"
                style={{
                  background: "linear-gradient(135deg, #D4AF37, #E8CC6A)",
                  color: "#0A1628",
                  boxShadow: "0 8px 30px rgba(212,175,55,0.3)"
                }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                Send Inquiry via WhatsApp · 透過WhatsApp發送查詢
              </button>

              <p className="text-center text-xs mt-4" style={{ color: "rgba(245,240,232,0.5)" }}>
                Your inquiry will be sent directly to our team via WhatsApp
              </p>
            </div>
          </form>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-16 md:py-24 px-6 md:px-12" style={{ background: "#0A1628" }}>
        <div className="max-w-4xl mx-auto text-center proj-reveal reveal">
          <h2 className="font-display text-3xl md:text-5xl font-bold text-white mb-6">
            Ready to Transform <span className="italic" style={{ color: "#D4AF37" }}>Your Space?</span>
          </h2>
          <p className="text-lg mb-8" style={{ color: "rgba(245,240,232,0.7)" }}>
            讓我們為您打造夢想家居 · Let us create your dream home
          </p>
          <a
            href="https://wa.me/85256614547"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 hover:scale-105"
            style={{
              background: "linear-gradient(135deg, #D4AF37, #E8CC6A)",
              color: "#0A1628",
              boxShadow: "0 8px 30px rgba(212,175,55,0.3)"
            }}>
            
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            Start Your Project · 開始您的項目
          </a>
        </div>
      </div>
    </section>);

}